import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LayoutDashboard,
  Package,
  FilePlus,
  FileText,
  Kanban,
  Users,
  Settings,
  BarChart3,
  Scissors,
  LogOut,
  ChevronRight,
  User,
} from "lucide-react";

// desktopOnly: true → visible only in desktop sidebar, hidden from mobile bottom nav
const NAV_ITEMS = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", short: "Home", roles: ["super_admin", "sales_manager", "production_manager", "accounts", "customer_admin", "customer_sub_user"] },
  { path: "/dashboard/create-order", icon: FilePlus, label: "Create Order", short: "Order", roles: ["customer_admin", "customer_sub_user"] },
  { path: "/dashboard/quotations", icon: FileText, label: "Pending Orders", short: "Pending", roles: ["super_admin", "sales_manager", "accounts", "customer_admin", "customer_sub_user"] },
  { path: "/dashboard/orders", icon: Kanban, label: "Confirmed Orders", short: "Orders", roles: ["super_admin", "sales_manager", "production_manager", "accounts", "customer_admin", "customer_sub_user"] },
  { path: "/dashboard/cutting-rates", icon: Scissors, label: "Cutting Rates", short: "Rates", roles: ["customer_admin", "customer_sub_user"] },
  // Desktop-only (masters + setup)
  { path: "/dashboard/products", icon: Package, label: "Products", short: "Products", roles: ["super_admin", "sales_manager"], desktopOnly: true },
  { path: "/dashboard/customers", icon: Users, label: "Customers", short: "Customers", roles: ["super_admin"], desktopOnly: true },
  { path: "/dashboard/settings", icon: Settings, label: "Settings", short: "Settings", roles: ["super_admin"], desktopOnly: true },
  { path: "/dashboard/analytics", icon: BarChart3, label: "Analytics", short: "Analytics", roles: ["super_admin", "sales_manager", "accounts"], desktopOnly: true },
];

const ROLE_LABELS = {
  super_admin: "Super Admin",
  sales_manager: "Sales Manager",
  production_manager: "Production Manager",
  accounts: "Accounts",
  customer_admin: "Customer Admin",
  customer_sub_user: "Customer User",
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => { logout(); navigate("/login"); };

  const allItems = NAV_ITEMS.filter(item => item.roles.includes(user?.role));
  const desktopItems = allItems; // full list in sidebar
  const mobileItems = allItems.filter(item => !item.desktopOnly);
  const hasMobileNav = mobileItems.length > 0;

  return (
    <div className="min-h-screen bg-slate-100">

      {/* ── MOBILE TOP BAR (< lg) ──────────────────────────────── */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 flex items-center justify-between px-4"
        style={{ height: "56px", paddingTop: "var(--safe-top)" }}>
        <img
          src="https://rimcore.in/images/rimcore_precut_white_transparent.png"
          alt="RIMCORE PRECUT"
          className="h-7"
        />
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold uppercase select-none">
            {user?.full_name?.[0] || <User size={14} />}
          </div>
        </div>
      </header>

      {/* ── DESKTOP SIDEBAR (≥ lg) ────────────────────────────── */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-full w-64 bg-slate-900 flex-col z-40">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800 flex-shrink-0">
          <img
            src="https://rimcore.in/images/rimcore_precut_white_transparent.png"
            alt="RIMCORE PRECUT"
            className="h-10"
            data-testid="sidebar-logo"
          />
        </div>

        {/* User */}
        <div className="p-4 border-b border-slate-800 flex-shrink-0">
          <p className="text-white font-medium truncate text-sm">{user?.full_name}</p>
          <p className="text-[11px] text-slate-400 uppercase tracking-wider mt-0.5">
            {ROLE_LABELS[user?.role] || user?.role}
          </p>
        </div>

        {/* Nav */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-0.5 px-3">
            {desktopItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/dashboard"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? "bg-orange-500 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <item.icon size={17} />
                <span>{item.label}</span>
                <ChevronRight size={13} className="ml-auto opacity-40" />
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-none text-xs uppercase tracking-wider"
            data-testid="logout-button"
          >
            <LogOut size={17} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────────── */}
      <main className={`lg:ml-64 min-h-screen pt-14 lg:pt-0 ${hasMobileNav ? "mobile-content-pad lg:pb-0" : ""}`}>
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* ── MOBILE BOTTOM NAV (< lg) ─────────────────────────── */}
      {hasMobileNav && (
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-800 flex items-stretch bottom-nav-safe"
          data-testid="mobile-bottom-nav"
        >
          {mobileItems.map(item => {
            const isActive = item.path === "/dashboard"
              ? location.pathname === "/dashboard"
              : location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/dashboard"}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 pt-2 transition-colors"
                style={{ minWidth: 0 }}
              >
                <item.icon
                  size={22}
                  className={isActive ? "text-orange-400" : "text-slate-400"}
                />
                <span className={`text-[9px] font-semibold uppercase tracking-wide leading-none ${isActive ? "text-orange-400" : "text-slate-500"}`}>
                  {item.short}
                </span>
                {isActive && <span className="w-1 h-1 rounded-full bg-orange-400 mt-0.5" />}
              </NavLink>
            );
          })}
          {/* Logout tab */}
          <button
            onClick={handleLogout}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 pt-2 text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="mobile-logout"
          >
            <LogOut size={22} />
            <span className="text-[9px] font-semibold uppercase tracking-wide leading-none">Out</span>
            <span className="w-1 h-1 mt-0.5 opacity-0" />
          </button>
        </nav>
      )}
    </div>
  );
}
